/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { PageProductTransferAdminPageResponse } from '../models/page-product-transfer-admin-page-response';
import { ProductTransferCreateRequest } from '../models/product-transfer-create-request';
import { ProductTransferStatusUpdateRequest } from '../models/product-transfer-status-update-request';

/**
 * Product transfer endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductTransfersService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation updateProductTransferStatus
   */
  static readonly UpdateProductTransferStatusPath =
    '/products/transfers/status';

  /**
   * Updates the status of the product transfers by admin.
   *
   * Updates the status of product transfers based on the provided list of product ids and a status.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateProductTransferStatus()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProductTransferStatus$Response(
    params: {
      body: ProductTransferStatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductTransfersService.UpdateProductTransferStatusPath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates the status of the product transfers by admin.
   *
   * Updates the status of product transfers based on the provided list of product ids and a status.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateProductTransferStatus$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateProductTransferStatus(
    params: {
      body: ProductTransferStatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateProductTransferStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getProductTransferRequests
   */
  static readonly GetProductTransferRequestsPath = '/products/transfers';

  /**
   * List the product transfers for administrators.
   *
   * List the product transfers for administrators in pageable format with filters
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getProductTransferRequests()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductTransferRequests$Response(
    params?: {
      /**
       * Search on the transfer type
       */
      transferRequesterType?: 'MANUFACTURER' | 'IMPORTER';

      /**
       * Search on packaging unit label
       */
      packagingUnitLabel?: string;

      /**
       * Search on the name of the from merchant
       */
      fromMerchantName?: string;

      /**
       * Search on the name of the to merchant
       */
      toMerchantName?: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageProductTransferAdminPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductTransfersService.GetProductTransferRequestsPath,
      'get'
    );
    if (params) {
      rb.query('transferRequesterType', params.transferRequesterType, {});
      rb.query('packagingUnitLabel', params.packagingUnitLabel, {});
      rb.query('fromMerchantName', params.fromMerchantName, {});
      rb.query('toMerchantName', params.toMerchantName, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageProductTransferAdminPageResponse>;
        })
      );
  }

  /**
   * List the product transfers for administrators.
   *
   * List the product transfers for administrators in pageable format with filters
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getProductTransferRequests$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getProductTransferRequests(
    params?: {
      /**
       * Search on the transfer type
       */
      transferRequesterType?: 'MANUFACTURER' | 'IMPORTER';

      /**
       * Search on packaging unit label
       */
      packagingUnitLabel?: string;

      /**
       * Search on the name of the from merchant
       */
      fromMerchantName?: string;

      /**
       * Search on the name of the to merchant
       */
      toMerchantName?: string;

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageProductTransferAdminPageResponse> {
    return this.getProductTransferRequests$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageProductTransferAdminPageResponse>) =>
          r.body as PageProductTransferAdminPageResponse
      )
    );
  }

  /**
   * Path part for operation createProductTransferRequests
   */
  static readonly CreateProductTransferRequestsPath = '/products/transfers';

  /**
   * Create product transfers.
   *
   * Create product transfers from specified packagingUnits
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createProductTransferRequests()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createProductTransferRequests$Response(
    params: {
      body: ProductTransferCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ProductTransfersService.CreateProductTransferRequestsPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Create product transfers.
   *
   * Create product transfers from specified packagingUnits
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createProductTransferRequests$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createProductTransferRequests(
    params: {
      body: ProductTransferCreateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.createProductTransferRequests$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }
}
